import { useEffect, useState, useRef } from "react";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import {
  CLEAR_BASKET_URL,
  GET_CONFIRMATION,
  GET_CONFIRMATION_DOCUMENT,
  GET_CONFIRMATION_TICKETS,
} from "../../api/apiRoutes";
import useFacility from "../../../utils/hooks/useFacility";
import { ConfirmationData } from "../../models/ConfirmationData";
import convertToPdf from "../../../utils/convertToPdf";
import { post } from "../../api";
import { errorMessageFromApi } from "../../utils/apiErrorMessages";
import { useLocation } from "react-router";
import qs from "qs";
import { sendPurchaseEvent, convertConfirmationDataToGtmFormat } from "../../../utils/Analitics/GtmEvents";
import ReactPixel from "react-facebook-pixel";
import { canSendToFacebookPixel } from "../../../utils/Analitics/analiticsConfigByFacility";

function getBillKeyFromUrl(search: string): string {
  // Preferuj k z URL-a TEJ strony — to jest jedyne miejsce, którego zawartość jest
  // ustalona raz, w momencie przekierowania (patrz useSuccessRedirection). sessionStorage
  // z parentPageUrl jest nadpisywany na bieżąco przez postMessage z rodzica (public/index.html),
  // więc do czasu, aż ten hook się odpali, może już nie zawierać oryginalnego k z powrotu z płatności.
  const billKeyParam = qs.parse(search.slice(1)).k;
  if (typeof billKeyParam === "string" && billKeyParam) {
    return billKeyParam;
  }

  const parentPageUrl = sessionStorage.getItem("parentPageUrl");
  if (parentPageUrl !== null) {
    try {
      const url = new URL(parentPageUrl);
      return url.searchParams.get("k") ?? "";
    } catch {
      return "";
    }
  }

  return "";
}

export default function useConfirmationData() {
  const { get } = useApiRequest();
  const location = useLocation();
  const facilityIds = useFacility();
  const [loading, setLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [billKey] = useState(() => getBillKeyFromUrl(location.search));
  const hasBillKey = Boolean(billKey);
  const purchaseEventSentRef = useRef(false);
  const [confirmationData, setConfirmationData] = useState({
    docNumber: "",
    name: "",
    lastName: "",
    email: "",
    phone: "",
    containsInvoice: false,
    containsTickets: false,
    billIds: "",
    discountCode: "",
    discountValue: 0,
    lines: [],
    payName: "",
    total: "",
    transactionId: "",
  } as ConfirmationData);

  const handleClearBasket = async () => {
    await post(CLEAR_BASKET_URL(facilityIds));
  };

  useEffect(() => {
    handleClearBasket();
    //eslint-disable-next-line
  }, []);

  const getConfirmationData = async () => {
    setLoading(true);
    const params = {
      facilityIds: `0,${facilityIds}`,
      billKey: billKey,
    };
    const { result, isError } = await get(GET_CONFIRMATION(params));
    if (!isError) {
      setConfirmationData(result);

      // Wysyłanie purchase event do GTM i Pixel po pobraniu danych potwierdzenia - tylko raz!
      if (!purchaseEventSentRef.current) {
        try {
          const purchaseData = convertConfirmationDataToGtmFormat(result);
          sendPurchaseEvent(facilityIds, purchaseData);

          if (canSendToFacebookPixel(facilityIds)) {
            ReactPixel.track("Purchase", {
              value: purchaseData.value,
              currency: "PLN",
              content_ids: purchaseData.items.map((item) => item.item_id),
              contents: purchaseData.items.map((item) => ({
                id: item.item_id,
                quantity: item.quantity,
              })),
              num_items: purchaseData.items.length,
            });
          }

          purchaseEventSentRef.current = true;
        } catch (error) {
          console.error("Error sending analytics purchase event:", error);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!hasBillKey) {
      return;
    }
    getConfirmationData();
    //eslint-disable-next-line
  }, [billKey, facilityIds, hasBillKey]);

  const getConfirmationDocument = async () => {
    if (!hasBillKey) {
      return;
    }
    setPdfError("");
    const params = {
      facilityIds: `0,${facilityIds}`,
      billKey: billKey,
    };
    const response = await get(GET_CONFIRMATION_DOCUMENT(params));
    if (!response.isError) {
      const pdf = convertToPdf(response.result);
      window.open(URL.createObjectURL(pdf));
      return;
    }
    setPdfError(errorMessageFromApi(response));
  };

  const getConfirmationTickets = async () => {
    setPdfError("");
    const params = {
      facilityIds: `0,${facilityIds}`,
      billKey: billKey,
    };
    const response = await get(GET_CONFIRMATION_TICKETS(params));
    if (!response.isError) {
      const pdf = convertToPdf(response.result);
      window.open(URL.createObjectURL(pdf));
      return;
    }
    setPdfError(errorMessageFromApi(response));
  };

  return {
    billKey,
    hasBillKey,
    loading,
    confirmationData,
    getConfirmationDocument,
    getConfirmationTickets,
    facilityIds,
    pdfError,
  };
}
